import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, from } from 'rxjs';
import {
    catchError,
    filter,
    map,
    switchMap,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import * as RA from 'ramda-adjunct';
import { ManagementApiService } from '../management-api.service';
import { ManagementStoreService } from '../management-store.service';
import { AddItemDialogComponent } from '@emm/shared/dialogs/add-item-dialog/add-item-dialog.component';
import {ItemsActions} from '@emm/management/store/items/items.actions';
import {DialogService} from '@emm/shared/services/dialog.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {ManagementBaseActions} from '@emm/management/store/base/management-base.actions';

@Injectable({
    providedIn: 'root'
})
export class ItemsEffects {

    loadBySize$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemsActions.loadBySize),
            switchMap(({ initSize }) =>
                this.api.getItemsBySize(initSize).pipe(
                    map((items) => ItemsActions.loadBySizeSuccess({ items })),
                    catchError(({ error }) => of(ItemsActions.loadBySizeFailure({ error })))
                )
            )
        )
    );

    loadBySizeSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemsActions.loadBySizeSuccess),
            switchMap(() => from([ItemsActions.loadAll()]))
        )
    );

    loadAll$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemsActions.loadAll),
            switchMap(() =>
                this.api.getAllItems().pipe(
                    map((items) => ItemsActions.loadAllSuccess({ items })),
                    catchError(({ error }) => of(ItemsActions.loadAllFailure({ error })))
                )
            )
        )
    );

    openAddDialog$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemsActions.openAddDialog),
            withLatestFrom(this.store.queryAttributes.allGlobal$, this.store.queryItemTypes.all$),
            filter(
                ([_, globalAttributes, itemTypes]) =>
                    RA.isNonEmptyArray(globalAttributes) && RA.isNonEmptyArray(itemTypes)
            ),
            switchMap(([_, globalAttributes, itemTypes]) => {
                const dialogRef = this.dialog.open(AddItemDialogComponent, {
                    width: '700px',
                    data: {
                        showCreateItem: true,
                        itemTypes,
                        globalAttributes,
                    },
                });

                return dialogRef.afterClosed().pipe(
                    filter((result) => result !== null && result === 'save'),
                    tap(() => {
                        this.notificationService.success('Added Successfully');
                    }),
                    map(() => ItemsActions.loadAll())
                );
            })
        )
    );

    delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemsActions.delete),
            switchMap(({ item }) =>
                this.dialogService
                    .openConfirmDialog('Are you sure you want to delete', item.name)
                    .afterClosed()
                    .pipe(
                        switchMap((confirmDelete) =>
                            confirmDelete
                                ? this.api.deleteItem(item.tag).pipe(
                                map(() => ItemsActions.deleteSuccess()),
                                catchError(({ error }) => of(ItemsActions.deleteFailure({ error })))
                                )
                                : of(ItemsActions.deleteFailure({ error: 'No Clicked' }))
                        )
                    )
            )
        )
    );

    deleteSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemsActions.deleteSuccess),
            tap(() => {
                this.notificationService.success('Deleted successfully');
            }),
            switchMap(() => of(ItemsActions.loadAll()))
        )
    );

    toggleEnabled$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemsActions.toggleEnabled),
            switchMap(({ item }) =>
                this.dialogService
                    .openConfirmDialog(
                        `Are you sure you want to ${!item.disabled ? 'disable' : 'enable'} ${item.name}?`,
                        null
                    )
                    .afterClosed()
                    .pipe(
                        switchMap((confirm) =>
                            confirm
                                ? this.api.setEnabled(item.tag, item.disabled).pipe(
                                map((_) => ItemsActions.toggleEnabledSuccess({ item })),
                                catchError(({ error }) => of(ItemsActions.toggleEnabledFailure({ error })))
                                )
                                : of(ItemsActions.toggleEnabledFailure({ error: 'No Clicked' }))
                        )
                    )
            )
        )
    );

    toggleEnabledSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemsActions.toggleEnabledSuccess),
            withLatestFrom(this.store.queryBase.itemDetail$),
            tap(([{ item }]) => {
                const title = !item.disabled ? 'Enabled' : 'Disabled';
                this.notificationService.success(`${title}: ${item.name}`);
            }),
            filter(([{ item }, itemDetail]) => RA.isNotNil(itemDetail) && itemDetail.tag === item.tag),
            switchMap(() => of(ManagementBaseActions.loadItemDetail()))
        )
    );

    constructor(
        private actions$: Actions,
        private api: ManagementApiService,
        public dialog: MatDialog,
        private dialogService: DialogService,
        private notificationService: NotificationService,
        private store: ManagementStoreService
    ) {}

}
